/**
 * Motivo do cancelamento do boleto em lista fechada (#6509). Antes era texto livre ("Atualizar o
 * boleto", "correção", "Alterar dados"...) e não dava para separar o que foi erro do Faturamento
 * do que foi pedido do cliente.
 *
 * Sem coluna nova: o texto gravado em `motivo_cancelamento_boleto` (e no histórico) começa pelo
 * rótulo do motivo ("Erro no valor ou no cálculo: faltou a bandeira"), e o caminho do Sicoob ainda
 * guarda o código no item do histórico. Assim os três caminhos — Sicoob/n8n, Asaas (edge) e a
 * fila automática — continuam iguais, e o indicador "erro e retrabalho" lê o histórico.
 *
 *   node --experimental-strip-types --test src/lib/__tests__/motivosCancelamentoBoleto.test.ts
 */

/** Erro e retrabalho do Faturamento: boletos cancelados por erro na emissão ÷ emitidos (plano de bônus). */
export const META_ERRO_RETRABALHO = 0.02;

export interface MotivoCancelamentoBoleto {
  codigo: string;
  rotulo: string;
  /** Conta no "erro e retrabalho" do Faturamento (meta até 2% dos boletos emitidos). */
  erroDoFaturamento: boolean;
  /** Exige o campo de detalhe. */
  exigeDetalhe?: boolean;
  /** Fica fora da conta (teste). */
  foraDaConta?: boolean;
  dica?: string;
}

export const MOTIVOS_CANCELAMENTO_BOLETO: readonly MotivoCancelamentoBoleto[] = [
  { codigo: 'vencimento', rotulo: 'Nova data de vencimento', erroDoFaturamento: false, dica: 'O cliente pediu outra data ou o boleto venceu e precisa ser atualizado.' },
  { codigo: 'juros_multa', rotulo: 'Incluir juros e multa por atraso', erroDoFaturamento: false },
  { codigo: 'acordo', rotulo: 'Acordo ou parcelamento', erroDoFaturamento: false },
  { codigo: 'conta_equatorial', rotulo: 'A conta da Equatorial mudou', erroDoFaturamento: false, dica: 'A Equatorial corrigiu ou reemitiu a conta depois da emissão.' },
  { codigo: 'saida_cliente', rotulo: 'Cliente saiu ou trocou de titular', erroDoFaturamento: false },
  { codigo: 'dados_cliente', rotulo: 'Erro nos dados do cliente', erroDoFaturamento: true, dica: 'Nome, CPF/CNPJ ou endereço errados no boleto.' },
  { codigo: 'valor', rotulo: 'Erro no valor ou no cálculo', erroDoFaturamento: true },
  { codigo: 'codigo_barras', rotulo: 'Saiu sem o código de barras da Equatorial', erroDoFaturamento: true },
  { codigo: 'duplicidade', rotulo: 'Boleto emitido em duplicidade', erroDoFaturamento: true },
  { codigo: 'conta_emissao', rotulo: 'Emitido na conta ou no banco errado', erroDoFaturamento: true },
  { codigo: 'teste', rotulo: 'Teste', erroDoFaturamento: false, foraDaConta: true },
  { codigo: 'outro', rotulo: 'Outro motivo', erroDoFaturamento: false, exigeDetalhe: true, dica: 'Descreva o motivo no campo abaixo.' },
];

const PORCODIGO = new Map(MOTIVOS_CANCELAMENTO_BOLETO.map(m => [m.codigo, m]));
export const motivoPorCodigo = (codigo: string | null | undefined) => (codigo ? PORCODIGO.get(codigo) ?? null : null);

/** Texto gravado na fatura: "Rótulo" ou "Rótulo: detalhe". */
export function textoDoMotivoCancelamento(codigo: string, detalhe: string): string {
  const m = motivoPorCodigo(codigo);
  const d = detalhe.trim();
  if (!m) return d;
  return d ? `${m.rotulo}: ${d}` : m.rotulo;
}

/** O que falta para cancelar: motivo escolhido e, quando o motivo pede, o detalhe. */
export function faltaNoMotivo(codigo: string | null | undefined, detalhe: string): string | null {
  const m = motivoPorCodigo(codigo);
  if (!m) return 'Escolha o motivo do cancelamento.';
  if (m.exigeDetalhe && !detalhe.trim()) return 'Descreva o motivo do cancelamento.';
  return null;
}

/** Cancelamentos automáticos (gatilho do banco e acordos), que nunca passam pela lista. */
const AUTOMATICOS: [RegExp, string][] = [
  [/^Fatura marcada como .* — cancelamento automático/i, 'acordo'],
  [/— boleto do próprio acordo$/i, 'acordo'],
];

/**
 * Motivo de um cancelamento: pelo código (caminho do Sicoob), pelo começo do texto (Asaas e
 * fila) ou pelos textos automáticos. Texto livre de antes da lista volta null.
 */
export function motivoDoCancelamento(texto: string | null | undefined, codigo?: string | null): MotivoCancelamentoBoleto | null {
  const porCodigo = motivoPorCodigo(codigo);
  if (porCodigo) return porCodigo;
  const t = (texto ?? '').trim();
  if (!t) return null;
  for (const m of MOTIVOS_CANCELAMENTO_BOLETO) {
    if (t === m.rotulo || t.startsWith(`${m.rotulo}:`)) return m;
  }
  for (const [re, c] of AUTOMATICOS) if (re.test(t)) return motivoPorCodigo(c);
  return null;
}

export interface CancelamentoDoHistorico {
  cancelado_em?: string | null;
  motivo_cancelamento_boleto?: string | null;
  motivo_codigo?: string | null;
}

export interface ErroERetrabalho {
  /** Cancelamentos no período (sem os de teste). */
  cancelamentos: number;
  /** Os que foram erro do Faturamento. */
  erros: number;
  /** Texto livre de antes da lista (não dá para classificar). */
  semMotivoDaLista: number;
  /**
   * Dá para medir: a maior parte dos cancelamentos do período tem motivo da lista. No mês em
   * que a lista começou, quase tudo é texto livre e a taxa sairia perto de zero sem ser.
   */
  mensuravel: boolean;
  porMotivo: { motivo: MotivoCancelamentoBoleto; qtd: number }[];
}

/**
 * Cancelamentos de boleto entre `inicio` (incluso) e `fim` (exclusivo), lidos do histórico de
 * cada fatura (uma fatura cancelada duas vezes conta duas).
 */
export function erroERetrabalho(historicos: readonly (readonly CancelamentoDoHistorico[] | null | undefined)[], inicio: Date, fim: Date): ErroERetrabalho {
  const r: ErroERetrabalho = { cancelamentos: 0, erros: 0, semMotivoDaLista: 0, mensuravel: true, porMotivo: [] };
  const conta = new Map<string, number>();
  for (const h of historicos) {
    for (const c of Array.isArray(h) ? h : []) {
      const t = c?.cancelado_em ? Date.parse(c.cancelado_em) : NaN;
      if (!Number.isFinite(t) || t < inicio.getTime() || t >= fim.getTime()) continue;
      const m = motivoDoCancelamento(c.motivo_cancelamento_boleto, c.motivo_codigo);
      if (m?.foraDaConta) continue;
      r.cancelamentos++;
      if (!m) { r.semMotivoDaLista++; continue; }
      if (m.erroDoFaturamento) r.erros++;
      conta.set(m.codigo, (conta.get(m.codigo) ?? 0) + 1);
    }
  }
  r.mensuravel = r.semMotivoDaLista * 2 < r.cancelamentos || r.cancelamentos === 0;
  r.porMotivo = [...conta.entries()].map(([c, qtd]) => ({ motivo: motivoPorCodigo(c)!, qtd })).sort((a, b) => b.qtd - a.qtd);
  return r;
}
